import type {FC} from 'react';
import React from 'react'
import _ from 'lodash'
import {ajax, getJson, postJson, resolveRoute} from '../helpers/ajax'
import * as kb from '../kb'
import SelectBox from './SelectBox'
import memoize from '../memoize'
import Icon from './Icon'
import icons from '../../markons/markons'
import classNames from 'classnames'
import GreyDash from './GreyDash'
import ForceScroll from './ForceScroll'
import {appendComponent} from '../helpers/react-helpers'
import ConflictsDialog from './ConflictsDialog'
import {FlexButtonBar} from './buttons'
import LoadingDots from './LoadingDots'
import {HttpStatusCode} from '../data/http-status-codes'
import {BtnCell, ItCell, ItHeader, LinkedIndexTable} from './tables'
import {NewActionButton} from './SaveSubmitButton'
import {ALERT_MAP, ALERT_OPTIONS, useSendAlert} from '../data/driver-alerts'
import {useStore} from '../helpers/react-form'
import {notifyError, notifySuccess} from '../notify'
import {NotSetText} from './grid-form'

import {arrayCount, mergeDefaults} from '../util/collections'

// must match BookingsController::getDriverTableData
export type ApiBookingDriversResponse = {
    segments: Segment[]
    canDispatch: boolean
    canEdit: boolean
    canViewAll: boolean
    drivers: [id: number, name: string][]
    isSingleVehicleBooking: boolean
    userId: string | number
}

const defaultApiBookingDriversResponse: ApiBookingDriversResponse = Object.freeze({
    segments: [],
    canDispatch: false,
    canEdit: false,
    canViewAll: false,
    drivers: [],
    isSingleVehicleBooking: true,
    userId: 0,
})

interface BookingDriverTableState extends ApiBookingDriversResponse {
    saving: boolean
    loading: boolean
}

interface BookingDriverTableProps {
    bookingId: number;
}

type DriverStatus = 'ASSIGNED' | 'DISPATCHED' | 'ACCEPTED'

type Segment = {
    id: number;
    number: string;
    vehicle?: Vehicle;
    driver?: Driver;
    sentDate?: string;
    openDate?: string;
    driverAlert?: string
}

type Vehicle = {
    code: string;
    name: string;
    style: Style;
}

type Style = {
    backgroundColor: string;
    color: string;
}

type Driver = {
    id: number;
    name: string;
    status: DriverStatus;
    phone: string;
}


interface AlertDropdownProps {
    segmentId: number
    onSuccess(res: ApiBookingDriversResponse): void
    alertId: string
}

const AlertDropdown: FC<AlertDropdownProps> = props => {

    const store = useStore({
        alertId: props.alertId,
    })
    const sendAlert = useSendAlert({
        onSuccess(res) {
            notifySuccess(res.smsRecipient ? `SMS sent to passenger at ${res.smsRecipient}` : "Alert saved")
            // store.set('alertId', '')
            if(props.onSuccess) {
                props.onSuccess(res.tableData);
            }
        },
        onError() {
            notifyError("Alert failed. Please try again.");
        }
    })

    return <>
        <SelectBox width={150} placeholder="— Alert —" options={ALERT_OPTIONS} {...store.input('alertId')}/>
        <NewActionButton className="btn-dispatch" title="Save alert"
                         onClick={() => sendAlert.mutate({segmentId: props.segmentId, alertId: store.get('alertId')})}
                         disabled={!store.get('alertId') || sendAlert.isLoading || props.alertId == store.get('alertId')}>
            <Icon name={icons.faBell}/>
        </NewActionButton>
    </>
}

interface AlertDropdownCellProps {
    segment: Segment
    onSuccess(res: ApiBookingDriversResponse): void
    userId: number
}

const AlertDropdownCell: FC<AlertDropdownCellProps> = ({segment:seg,...props}) => {
    // {seg.driver ? (seg.driverAlert ? <span>{ALERT_MAP[seg.driverAlert] || "Unknown"}</span> : <NotSetText>None Sent</NotSetText>) : <GreyDash/>}

    if(!seg.driver) {
        return <ItCell center><GreyDash/></ItCell>
    }

    if(seg.driver.id == props.userId && seg.driver.status === 'ACCEPTED') {
        return <BtnCell noWrap center><AlertDropdown alertId={seg.driverAlert} segmentId={seg.id} onSuccess={props.onSuccess}/></BtnCell>
    }

    if(seg.driverAlert) {
        return <ItCell center>{ALERT_MAP[seg.driverAlert] || "Unknown"}</ItCell>
    }

    return <ItCell center><NotSetText>None</NotSetText></ItCell>
}

interface DriverTableActions {
    segment: Segment
    canDispatch: boolean
    isSaving: boolean
    userId: number
    onDriverAccept: React.EventHandler<any>
    onDriverReject: React.EventHandler<any>
    onDispatch: React.EventHandler<any>
}

const DriverActionsCell: FC<DriverTableActions> = ({segment:seg,...props}) => {

    if(!seg.driver) {
        return <ItCell center><GreyDash/></ItCell>
    }

    const isDriver = seg.driver.id == props.userId

    if(isDriver && seg.driver.status == 'DISPATCHED') {
        return (
            <BtnCell>
                <button className="btn btn-save" disabled={props.isSaving} onClick={props.onDriverAccept}>
                    Accept
                </button>
                <button className="btn btn-delete" disabled={props.isSaving} onClick={props.onDriverReject}>
                    Reject
                </button>
            </BtnCell>
        )
    }

    if(props.canDispatch) {
        return (
            <BtnCell>
                <NewActionButton disabled={props.isSaving} onClick={props.onDispatch} width={90}>
                    <Icon name={icons.faPaperPlane}/> {seg.driver.status === 'ASSIGNED' ? "Dispatch" : "Update"}
                </NewActionButton>
            </BtnCell>
        )
    }

    return <ItCell center><GreyDash/></ItCell>
}


export default class BookingDriverTable extends React.PureComponent<BookingDriverTableProps, BookingDriverTableState> {

    handleDriverChanged = memoize<[Segment], React.ChangeEventHandler<HTMLSelectElement>>(seg => ev => {
        const selectBox = ev.currentTarget

        if (seg.driver && (seg.driver.status === 'DISPATCHED' || seg.driver.status === 'ACCEPTED')) {
            if (!window.confirm(`This driver has already ${seg.driver.status === 'DISPATCHED' ? 'been dispatched' : 'accepted the job'}. Are you sure want to change the driver for this booking?`)) {
                return
            }
        }

        this.setState<'saving'>({saving: true})
        const driverId = ev.target.value ? Number(ev.target.value) : null

        const doChangeDriver = (ignoreConflicts: boolean) => {
            postJson(resolveRoute('api.changeDriver', {id: this.props.bookingId}), {
                segmentId: this.state.isSingleVehicleBooking ? null : seg.id,
                driverId,
                ignoreConflicts,
            }).finally(() => {
                this.setState<'saving'>({saving: false})
            }).then((res: any) => {
                if (res.ok) {
                    this.setState<'segments'>({
                        segments: res.data.segments,
                    })
                    kb.notify({
                        text: "Driver changed",
                        className: 'success',
                    })
                } else if (res.status === HttpStatusCode.Conflict) {
                    appendComponent(({unmount}) => (
                        <ConflictsDialog unmount={() => {
                            unmount()
                            // FIXME: seg.driver is sometimes null: https://sentry.io/share/issue/c0fcf8d876f44f1eb0f9a6727ad33495/
                            // Assigning selectBox.value to `null` will make it go blank, '' will select TBD
                            selectBox.value = seg.driver?.id ?? ''
                        }} assign={() => {
                            unmount()
                            doChangeDriver(true)
                        }} conflicts={res.data.conflicts}/>)
                    )
                } else {
                    kb.notify({
                        text: "Error changing driver",
                        className: 'error',
                    })
                }
            })
        }

        doChangeDriver(false)
    })

    handleDispatch = memoize<[number], React.EventHandler<any>>(segmentId => ev => {
        this.setState<'saving'>({saving: true})
        const seg = _.find(this.state.segments, seg => seg.id == segmentId)

        postJson(resolveRoute('api.dispatchDriver', {id: this.props.bookingId}), {
            segmentId: this.state.isSingleVehicleBooking ? null : seg.id,
            driverId: seg.driver.id,
        }).finally(() => {
            this.setState<'saving'>({saving: false})
        }).then((res: any) => {
            if (res.ok) {
                kb.notify({
                    text: res.data.newJob ? "Driver dispatched" : "Driver update email sent",
                    className: 'success',
                })
                this.setState<'segments'>({
                    segments: res.data.segments,
                })
            } else {
                kb.notify({
                    text: "Error dispatching driver",
                    className: 'error',
                })
            }
        })
    })

    handleAlertSuccess = (tableData: ApiBookingDriversResponse) => {
        if(tableData) {
            this.setState<keyof ApiBookingDriversResponse>(mergeDefaults(tableData,defaultApiBookingDriversResponse));
        }
    }

    handleDriverResponse = memoize<[number | null, 'accept' | 'reject'], React.EventHandler<any>>((segmentId, response) => (ev) => {
        this.setState<'saving'>({saving: true})
        // let seg = _.find(this.state.segments, seg => seg.id == segmentId);

        const url = segmentId
            ? resolveRoute('driverSegmentResponse', {segment_id: segmentId, response})
            : resolveRoute('driverBookingResponse', {booking_id: this.props.bookingId, response})
        getJson(url).finally(() => {
            this.setState<'saving'>({saving: false})
        }).then((res: any) => {
            if (res.ok) {
                kb.notify({
                    text: response === 'accept' ? 'Job accepted' : 'Job rejected',
                    className: 'success',
                })
                this.setState<'segments'>({
                    segments: res.data.segments,
                })
                // if (!this.state.canViewAll && response === 'reject') {
                //     kb.redirect(Router.route('bookings.index'))
                // }
            } else {
                kb.notify({
                    text: "Error responding to job",
                    className: 'error',
                })
            }
        })
    })

    constructor(props: any) {
        super(props)
        this.state = {
            loading: true,
            saving: false,
            segments: [],
            drivers: [],
            canDispatch: false,
            canEdit: false,
            canViewAll: false,
            userId: 0,
            isSingleVehicleBooking: true,
        }
    }

    componentDidMount() {
        ajax(resolveRoute('api.bookingDrivers', {
            id: this.props.bookingId,
        })).then((res: any) => {
            if (res.ok) {
                this.setState<keyof Omit<BookingDriverTableState, 'saving'>>({
                    ...res.data,
                    loading: false,
                })
            } else {
                kb.notify({
                    text: "Error loading driver(s)",
                    className: 'error',
                })
            }
        })
    }

    render() {
        if (this.state.loading) {
            return <LoadingDots height={50}/>
        }

        const dispatchCount = arrayCount(this.state.segments, seg => seg.driver?.id == this.state.userId && seg.driver.status === 'DISPATCHED')

        return (
            <>
                <ForceScroll>
                    <LinkedIndexTable>
                        <thead>
                            <tr>
                                <ItHeader center width="50px">Seg.</ItHeader>
                                <ItHeader center width="71px">Unit</ItHeader>
                                <ItHeader center width="140px">Driver</ItHeader>
                                <ItHeader center width="100px">Driver #</ItHeader>
                                <ItHeader center width="1px">Actions</ItHeader>
                                <ItHeader center width="110px">Alerts</ItHeader>
                                <th/>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.segments.map(seg => {
                                const driverSpan = seg.driver
                                    ? <span style={{width: '110px'}}
                                            className={`${kb.driverStatusToClass(seg.driver.status)} ellipsis`}>{seg.driver.name}</span>
                                    : <span className="not-set">TBD</span>


                                return (
                                    <tr key={seg.id}>
                                        <td className="tcenter" style={{color: '#B70103', fontWeight: 'bold'}}>
                                            <span>{seg.number}</span>
                                        </td>
                                        <td className="tcenter"
                                            style={seg.vehicle ? seg.vehicle.style : undefined}>{seg.vehicle ?
                                            <span title={seg.vehicle.name}>{seg.vehicle.code}</span> :
                                            <span className="not-set">TBD</span>}
                                        </td>
                                        {this.state.canEdit
                                            ? (
                                                <td>
                                                    <SelectBox value={seg.driver?.id ?? ''}
                                                               disabled={this.state.saving}
                                                               onChange={this.handleDriverChanged(seg)}
                                                               className={classNames(seg.driver ? kb.driverStatusToClass(seg.driver.status) : 'not-set', 'change-driver-select')}>
                                                        <option value="" className="not-set">TBD</option>
                                                        {this.state.drivers.map(([id, name]) => (
                                                            <option key={id} value={id}>{name}</option>
                                                        ))}
                                                    </SelectBox>
                                                </td>
                                            )
                                            : <td>{driverSpan}</td>
                                        }
                                        <td>{seg.driver ? (seg.driver.phone ?
                                                <span className="nowrap">{seg.driver.phone}</span> :
                                                <span className="not-set">Not Set</span>) :
                                            <GreyDash/>}</td>

                                        <DriverActionsCell
                                            segment={seg}
                                            userId={this.state.userId}
                                            onDriverAccept={this.handleDriverResponse(seg.id, 'accept')}
                                            onDriverReject={this.handleDriverResponse(seg.id, 'reject')}
                                            canDispatch={this.state.canDispatch}
                                            isSaving={this.state.saving}
                                            onDispatch={this.handleDispatch(seg.id)}
                                        />

                                        <AlertDropdownCell segment={seg} onSuccess={this.handleAlertSuccess} userId={this.state.userId}/>
                                        <td/>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </LinkedIndexTable>
                </ForceScroll>
                {dispatchCount > 1 ? (
                    <FlexButtonBar noWrap marginTop="5px">
                        <button className="btn btn-save" disabled={this.state.saving}
                                onClick={this.handleDriverResponse(null, 'accept')}>Accept All
                        </button>
                        <button className="btn btn-delete" disabled={this.state.saving}
                                onClick={this.handleDriverResponse(null, 'reject')}>Reject All
                        </button>
                    </FlexButtonBar>
                ) : null}
            </>
        )
    }
}

